import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterAttributes } from "components/generalComponents/filterComponents/model/FilterComponentsModel";
import { PartnerResponseEntity } from "features/errorCodeGeneralFeature/domain/entities/PartnerResponseEntity";
import { TransactionDetailReportResponseEntity } from "features/generals/domain/entities/TransactionDetailReportResponseEntity";
import { TransactionReportResponseEntity } from "features/generals/domain/entities/TransactionReportResponseEntity";
import { isEmpty, isString } from "lodash";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { CurrencyEnum } from "utils/enums/CurrencyEnum";
import { checkLowerCase } from "utils/format/StringFormat";
import { formatCurrency } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { GeneralRangeType, GeneralStatCards } from "../interfaces/IGenerals";

export class GeneralConstants {
  static filters(
    localString: typeof strings,
    partnerList: Array<PartnerResponseEntity>,
    carrierList: Array<BaseFilterListResponseModel>,
    currencyOptions: Array<{ label?: string; value?: any }> = [
      { label: "VND", value: "VND" },
    ],
    handlePartnerChange?: (partnerCode: string) => void,
    storeOptions: Array<{ label?: string; value?: any }> = [],
    staffOptions: Array<{ label?: string; value?: any }> = [],
    selectedPartnerType?: number,
    isStore?: boolean,
    isAllStore?: boolean,
    handleStoreChange?: (storeCode: string) => void,
    handleStaffChange?: (staff: string[]) => void,
  ) {
    const filters = [
      {
        colLg: 4,
        colDefault: 8,
        defaultValue: "VND",
        filterName: "currency",
        filterType: "select",
        labelName: localString.General.ByCurrency,
        placeholder: localString.General.ByCurrency,
        options: currencyOptions,
      },
      {
        colLg: 9,
        colDefault: 8,
        defaultValue: "",
        filterName: "partner",
        filterType: "select",
        labelName: localString.GlobalLabels.Partner,
        placeholder: localString.GlobalPlaceholder.Partner,
        customKey: ["email", "phone"],
        needCapaChecking: true,
        options: [
          {
            label: localString.GlobalLabels.All,
            value: "",
          },
          ...partnerList?.map((item) => ({
            label: `${checkLowerCase(
              item?.name,
            )}-${item?.providerCode?.toUpperCase()}`,
            value:
              isString(item?.partnerRefCode) && !isEmpty(item?.partnerRefCode)
                ? item?.partnerRefCode
                : item?.code,
            email: item?.email,
            phone: item?.phone,
            type: item?.type,
            isStore: item?.isStore,
            providerCode: item?.providerCode,
            code: item?.code,
          })),
        ],
        onChange: handlePartnerChange,
      },
      {
        colLg: 5,
        colDefault: 8,
        defaultValue: [""],
        filterName: "telco",
        filterType: "select-multiple",
        needCapaChecking: true,
        labelName: localString.GlobalLabels.Carrier,
        placeholder: localString.GlobalLabels.Carrier,
        options: [
          {
            label: localString.GlobalLabels.All,
            value: "",
          },
          ...carrierList?.map((item) => ({
            label: item?.name,
            value: item?.code,
          })),
        ],
      },
    ] as Array<FilterAttributes>;

    if (selectedPartnerType === 1 && isStore) {
      filters.splice(
        3,
        0,
        {
          colLg: 6,
          colDefault: 8,
          defaultValue: "",
          filterName: "store",
          filterType: "select",
          labelName: localString.GlobalLabels.Store,
          capabilityKey: CapabilityEnum.storeFilter,
          needCapaChecking: true,
          options: storeOptions,
          onChange: handleStoreChange,
          placeholder: localString.GlobalLabels.Choose,
        } as FilterAttributes,
        {
          colLg: 5,
          colDefault: 8,
          defaultValue: [""],
          filterName: "staff",
          filterType: "select-multiple",
          labelName: localString.GlobalLabels.Staff,
          capabilityKey: CapabilityEnum.staffFilter,
          needCapaChecking: true,
          options: staffOptions,
          disable: isAllStore,
          onChange: handleStaffChange,
          placeholder: localString.GlobalLabels.Choose,
        } as FilterAttributes,
      );
    }

    return filters;
  }

  static defaultStatCards = (
    currencyCode: string = "VND",
  ): GeneralStatCards => ({
    todayRevenue: {
      currentTime: "",
      previousTime: "",
      value: formatCurrency(0, true, true, this.getCurrencyEnum(currencyCode)),
      change: "",
      trend: "up",
    },
    monthRevenue: {
      currentTime: "",
      previousTime: "",
      value: formatCurrency(0, true, true, this.getCurrencyEnum(currencyCode)),
      change: "",
      trend: "up",
    },
    previousMonthRevenue: {
      currentTime: "",
      previousTime: "",
      value: formatCurrency(0, true, true, this.getCurrencyEnum(currencyCode)),
      change: "",
      trend: "up",
    },
    todayTransactions: {
      currentTime: "",
      previousTime: "",
      value: "0",
      change: "",
      trend: "up",
    },
    monthTransactions: {
      currentTime: "",
      previousTime: "",
      value: "0",
      change: "",
      trend: "up",
    },
    previousMonthTransactions: {
      currentTime: "",
      previousTime: "",
      value: "0",
      change: "",
      trend: "up",
    },
  });

  static getCurrencyEnum = (currencyCode?: string) =>
    String(currencyCode || "VND").toUpperCase() === "USD"
      ? CurrencyEnum.$
      : CurrencyEnum.VND;

  static formatChangeRate = (rate?: number) => {
    if (rate === undefined || rate === null) return "";
    const sign = rate >= 0 ? "+" : "";
    return `${sign}${rate.toFixed(1)}%`;
  };

  static getDisplayStatByCurrency = <T>(
    data: Array<{ currency?: string; displayStat?: Array<T> }> = [],
    selectedCurrency = "VND",
  ): Array<T> => {
    const matchedItem = data.find(
      (item) =>
        String(item?.currency || "").toUpperCase() ===
        String(selectedCurrency || "VND").toUpperCase(),
    );

    return matchedItem?.displayStat || [];
  };

  static formatPeriodLabel = (time: string, range: GeneralRangeType) => {
    const trimmed = String(time).trim();
    const weekNum = Number(trimmed);
    const d = new Date(trimmed);
    const isValidDate = !isNaN(d.getTime());

    if (range === "day") {
      if (!isValidDate) return trimmed;
      return time;
    }

    if (range === "week") {
      if (!isNaN(weekNum) && /^\d+$/.test(trimmed)) {
        return `${strings.General.Revenue.WeekOfYear} ${weekNum}`;
      }
      if (!isValidDate) return trimmed;
      const dayIdx = d.getDay();
      const diff = (dayIdx === 0 ? -6 : 1) - dayIdx;
      d.setDate(d.getDate() + diff);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${strings.General.Revenue.WeekOfYear} ${day}-${month}-${year}`;
    }

    if (!isValidDate) return trimmed;
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${year}`;
  };

  static extractCurrencies = (
    data:
      | Array<{ currency?: string }>
      | Array<TransactionReportResponseEntity>
      | Array<TransactionDetailReportResponseEntity>
      | undefined,
  ) => {
    return Array.from(
      new Set(
        (data || [])
          .map((item) =>
            String(item?.currency || "")
              .toUpperCase()
              .trim(),
          )
          .filter(Boolean),
      ),
    );
  };
}
